import type { DiningArea, DiningTable, TableStatus } from '@horevia/contracts';
import {
  Button,
  Container,
  EmptyState,
  ErrorState,
  FloorLegend,
  FloorPlan,
  FloorTableTile,
  NoPermissionState,
  PageHeader,
  Skeleton,
} from '@horevia/ui';
import { useQuery } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { LayoutGrid, Pencil, Plus } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSession } from '@/features/auth/session';
import { AreaDrawer, TableDrawer } from '@/features/pos/TableDrawers';
import { useCurrentOutlet } from '@/features/pos/outlet';
import {
  areasQueryOptions,
  openOrdersQueryOptions,
  tablesQueryOptions,
} from '@/features/pos/queries';
import { tableStatusOf } from '@/features/pos/status';
import { errorMessage, errorTraceId } from '@/lib/errors';
import { formatAmount, formatTime } from '@/lib/format';

export const Route = createFileRoute('/_app/restaurant/tables')({ component: TablesPage });

/**
 * Plan de salle (charte §9.7) : tables par salle, couleur d'état `--tb-*`,
 * commande ouverte (montant, durée) ; clic = ouvrir ou reprendre la commande ;
 * mode édition pour créer et modifier salles et tables.
 */
function TablesPage() {
  const { t, i18n } = useTranslation(['pos', 'common', 'errors']);
  const session = useSession();
  const navigate = useNavigate();
  const { outlet, outlets, setOutletId, isPending: outletsPending } = useCurrentOutlet();
  const canRead = session.can('table.read');
  const canManage = session.can('table.manage');
  const canOrder = session.can('order.create');
  const [editing, setEditing] = useState(false);
  const [tableDrawer, setTableDrawer] = useState<{ open: boolean; table: DiningTable | null }>({
    open: false,
    table: null,
  });
  const [areaDrawer, setAreaDrawer] = useState<{ open: boolean; area: DiningArea | null }>({
    open: false,
    area: null,
  });

  const areas = useQuery(areasQueryOptions(outlet?.id ?? '', Boolean(outlet) && canRead, editing));
  const tables = useQuery(
    tablesQueryOptions(outlet?.id ?? '', Boolean(outlet) && canRead, editing),
  );
  const orders = useQuery(
    openOrdersQueryOptions(outlet?.id ?? '', Boolean(outlet) && session.can('order.read')),
  );

  const orderByTable = useMemo(
    () =>
      new Map(
        (orders.data ?? [])
          .filter((order) => order.tableId)
          .map((order) => [order.tableId!, order]),
      ),
    [orders.data],
  );
  const areaList = useMemo(() => areas.data ?? [], [areas.data]);
  const tableList = useMemo(() => tables.data ?? [], [tables.data]);
  const groups = useMemo<{ area: DiningArea | null; tables: DiningTable[] }[]>(
    () =>
      [
        ...areaList.map((area) => ({
          area,
          tables: tableList.filter((table) => table.areaId === area.id),
        })),
        {
          area: null,
          tables: tableList.filter(
            (table) => !table.areaId || !areaList.some((area) => area.id === table.areaId),
          ),
        },
      ].filter((group) => group.tables.length > 0 || (editing && group.area)),
    [areaList, tableList, editing],
  );

  if (!canRead) {
    return (
      <Container>
        <NoPermissionState
          title={t('common:states.noPermissionTitle')}
          description={t('common:states.noPermissionDescription')}
        />
      </Container>
    );
  }

  const onTable = (table: DiningTable) => {
    if (editing) {
      setTableDrawer({ open: true, table });
      return;
    }
    const order = orderByTable.get(table.id);
    if (order) void navigate({ to: '/pos', search: { order: order.id } });
    else if (canOrder) void navigate({ to: '/pos', search: { table: table.id } });
  };

  return (
    <Container>
      <PageHeader
        title={t('pos:tables.title')}
        subtitle={t('pos:tables.subtitle')}
        breadcrumb={[
          { label: t('common:appName'), link: <Link to="/">{t('common:appName')}</Link> },
          { label: t('pos:tables.title') },
        ]}
        actions={
          <div className="row row--wrap" style={{ gap: 8 }}>
            {outlets.length > 1 && outlet ? (
              <select
                className="select"
                aria-label={t('pos:bar.outlet')}
                value={outlet.id}
                onChange={(event) => setOutletId(event.target.value)}
              >
                {outlets.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name}
                  </option>
                ))}
              </select>
            ) : null}
            {canManage && outlet ? (
              <>
                {editing ? (
                  <>
                    <Button
                      variant="secondary"
                      onClick={() => setAreaDrawer({ open: true, area: null })}
                    >
                      <Plus className="ico" aria-hidden="true" />
                      {t('pos:tables.newArea')}
                    </Button>
                    <Button
                      variant="secondary"
                      onClick={() => setTableDrawer({ open: true, table: null })}
                    >
                      <Plus className="ico" aria-hidden="true" />
                      {t('pos:tables.newTable')}
                    </Button>
                  </>
                ) : null}
                <Button
                  variant={editing ? 'primary' : 'secondary'}
                  onClick={() => setEditing((value) => !value)}
                >
                  <Pencil className="ico" aria-hidden="true" />
                  {editing ? t('pos:tables.done') : t('pos:tables.edit')}
                </Button>
              </>
            ) : null}
          </div>
        }
      />

      <FloorLegend
        items={(['FREE', 'OCCUPIED', 'BILL_REQUESTED'] as TableStatus[]).map((tone) => ({
          tone,
          label: t(`pos:tables.legend.${tone}`),
        }))}
      />

      {outletsPending || tables.isPending ? (
        <Skeleton height={260} />
      ) : !outlet ? (
        <EmptyState
          icon={<LayoutGrid className="ico" aria-hidden="true" />}
          title={t('pos:noOutlet.title')}
          description={t('pos:noOutlet.body')}
        />
      ) : tables.isError ? (
        <ErrorState
          title={t('common:states.errorTitle')}
          description={errorMessage(tables.error, t)}
          traceId={errorTraceId(tables.error)}
          retryLabel={t('common:actions.retry')}
          onRetry={() => void tables.refetch()}
          traceCopyLabel={t('common:actions.copy')}
          traceCopiedLabel={t('common:actions.copied')}
        />
      ) : groups.length === 0 ? (
        <EmptyState
          icon={<LayoutGrid className="ico" aria-hidden="true" />}
          title={t('pos:tables.empty')}
          description={t('pos:tables.emptyHint')}
          action={
            canManage ? (
              <Button variant="primary" onClick={() => setEditing(true)}>
                {t('pos:tables.edit')}
              </Button>
            ) : undefined
          }
        />
      ) : (
        <div className="stack" data-density="pos">
          {groups.map((group) => (
            <section key={group.area?.id ?? 'none'} className="stack" style={{ gap: 8 }}>
              <h2 className="h5" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {group.area?.name ?? t('pos:tables.noArea')}
                {editing && group.area ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setAreaDrawer({ open: true, area: group.area })}
                  >
                    <Pencil className="ico ico-16" aria-hidden="true" />
                    {t('pos:tables.editArea')}
                  </Button>
                ) : null}
              </h2>
              <FloorPlan>
                {group.tables.map((table) => {
                  const order = orderByTable.get(table.id);
                  const status = tableStatusOf(order);
                  return (
                    <FloorTableTile
                      key={table.id}
                      code={table.code}
                      status={status}
                      shape={table.shape}
                      inactive={!table.isActive}
                      aria-label={`${table.code} — ${t(`pos:tables.legend.${status}`)}`}
                      meta={
                        order
                          ? `#${order.dailyNumber} · ${t('pos:tables.since', { time: formatTime(order.openedAt, i18n.language) })}`
                          : t('pos:tables.seats', { count: table.seats })
                      }
                      amount={
                        order
                          ? formatAmount(order.total, i18n.language, order.currencyCode)
                          : undefined
                      }
                      onClick={() => onTable(table)}
                    />
                  );
                })}
              </FloorPlan>
            </section>
          ))}
        </div>
      )}

      {outlet ? (
        <>
          <TableDrawer
            open={tableDrawer.open}
            onOpenChange={(open) => setTableDrawer((current) => ({ ...current, open }))}
            outletId={outlet.id}
            areas={areaList}
            table={tableDrawer.table}
          />
          <AreaDrawer
            open={areaDrawer.open}
            onOpenChange={(open) => setAreaDrawer((current) => ({ ...current, open }))}
            outletId={outlet.id}
            area={areaDrawer.area}
          />
        </>
      ) : null}
    </Container>
  );
}
